import {
  useState,
  MouseEvent,
  KeyboardEvent,
  useEffect,
  useCallback,
  useRef,
} from 'react';

import {
  Badge,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { NameInput } from './components/NameInput';
import { ViewItem, ViewActionsButton } from './components/ViewItem';
import { ViewLink } from './components/ViewLink';
import { ViewsContainer } from './components/ViewsContainer';
import styles from './Views.module.scss';
import type {
  FilterView,
  useCreateFilterView,
  useUpdateFilterView,
  useDeleteFilterView,
} from '@/api/REST/filterViews';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useNotification } from '@/hooks/useNotification';

interface ViewsProps {
  filterViews: FilterView[];
  createView: ReturnType<typeof useCreateFilterView>; // Still needed to check isPending state
  createEmptyView: (name?: string) => Promise<FilterView>;
  saveAsNewView: (name?: string) => Promise<FilterView>;
  duplicateView: (view: FilterView) => Promise<FilterView>;
  updateView: ReturnType<typeof useUpdateFilterView>;
  deleteViewMutation: ReturnType<typeof useDeleteFilterView>;
  currentViewId?: string | null;
  hasUnsavedChanges?: boolean;
  onViewSelect?: (view: FilterView | null) => void;
  isCreatingView?: boolean;
  isSavingAsNewView?: boolean;
  onCreateStart?: () => void;
  onCreateEnd?: () => void;
  onSaveAsNewEnd?: () => void;
  // Filter panel toggle
  filterPanelOpen?: boolean;
  onToggleFilterPanel?: () => void;
}

export const Views = ({
  filterViews,
  createView,
  createEmptyView,
  saveAsNewView,
  duplicateView,
  updateView,
  deleteViewMutation,
  currentViewId,
  hasUnsavedChanges,
  onViewSelect,
  isCreatingView,
  isSavingAsNewView,
  onCreateStart,
  onCreateEnd,
  onSaveAsNewEnd,
  filterPanelOpen,
  onToggleFilterPanel,
}: ViewsProps) => {
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('views');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedView, setSelectedView] = useState<FilterView | null>(null);
  const [renamingViewId, setRenamingViewId] = useState<string | null>(null);
  const [viewToDuplicateLocal, setViewToDuplicateLocal] =
    useState<FilterView | null>(null);
  const isCreatingRef = useRef(false);
  const isSavingAsNewRef = useRef(false);
  const isDuplicatingRef = useRef(false);

  const { showConfirm } = useConfirmDialog();

  const handleCreateView = useCallback(async () => {
    try {
      // Use createEmptyView from the manager - it handles name generation and all the logic
      const newView = await createEmptyView();

      // Enter rename mode for the newly created view
      setRenamingViewId(newView.filterViewId);

      // Auto-select the new view
      if (onViewSelect) {
        onViewSelect(newView);
      }

      // Notify parent that creation is complete
      if (onCreateEnd) {
        onCreateEnd();
      }
    } catch (error) {
      console.error('Failed to create view:', error);
      if (onCreateEnd) {
        onCreateEnd();
      }
    }
  }, [createEmptyView, onCreateEnd, onViewSelect]);

  const handleSaveAsNew = useCallback(async () => {
    try {
      // Use saveAsNewView from the manager - it handles name generation and saves with current filters
      const newView = await saveAsNewView();

      // Enter rename mode for the newly created view
      setRenamingViewId(newView.filterViewId);

      // Auto-select the new view
      if (onViewSelect) {
        onViewSelect(newView);
      }

      // Notify parent that save is complete
      if (onSaveAsNewEnd) {
        onSaveAsNewEnd();
      }
    } catch (error) {
      console.error('Failed to save as new view:', error);
      if (onSaveAsNewEnd) {
        onSaveAsNewEnd();
      }
    }
  }, [saveAsNewView, onSaveAsNewEnd, onViewSelect]);

  const handleDuplicateView = useCallback(async () => {
    if (!viewToDuplicateLocal) return;

    try {
      // Use duplicateView from the manager
      const newView = await duplicateView(viewToDuplicateLocal);

      // Enter rename mode for the duplicated view
      setRenamingViewId(newView.filterViewId);

      // Auto-select the new view
      if (onViewSelect) {
        onViewSelect(newView);
      }

      // Clear the duplicate trigger
      setViewToDuplicateLocal(null);
    } catch (error) {
      console.error('Failed to duplicate view:', error);
      // Clear the duplicate trigger even on error
      setViewToDuplicateLocal(null);
    }
  }, [duplicateView, viewToDuplicateLocal, onViewSelect]);

  // Handle view creation when isCreatingView changes
  useEffect(() => {
    if (isCreatingView && !createView.isPending && !isCreatingRef.current) {
      isCreatingRef.current = true;
      handleCreateView();
    }

    // Reset the ref when creation is complete
    if (!isCreatingView && isCreatingRef.current) {
      isCreatingRef.current = false;
    }
  }, [isCreatingView, createView.isPending, handleCreateView]);

  // Handle saving as new view when isSavingAsNewView changes
  useEffect(() => {
    if (
      isSavingAsNewView &&
      !createView.isPending &&
      !isSavingAsNewRef.current
    ) {
      isSavingAsNewRef.current = true;
      handleSaveAsNew();
    }

    // Reset the ref when save is complete
    if (!isSavingAsNewView && isSavingAsNewRef.current) {
      isSavingAsNewRef.current = false;
    }
  }, [isSavingAsNewView, createView.isPending, handleSaveAsNew]);

  // Trigger duplicate when viewToDuplicateLocal is set
  useEffect(() => {
    if (
      viewToDuplicateLocal &&
      !createView.isPending &&
      !isDuplicatingRef.current
    ) {
      isDuplicatingRef.current = true;
      handleDuplicateView();
    }

    // Reset the ref when duplication is complete
    if (!viewToDuplicateLocal && isDuplicatingRef.current) {
      isDuplicatingRef.current = false;
    }
  }, [viewToDuplicateLocal, createView.isPending, handleDuplicateView]);

  const handleRenameConfirm = async (viewId: string, newName: string) => {
    try {
      // Find the view to preserve its filterParameters and sortOrder
      const view = filterViews.find((v) => v.filterViewId === viewId);
      if (!view) return;

      await updateView.mutateAsync({
        id: viewId,
        data: {
          name: newName,
          filterParameters: view.filterParameters,
          sortOrder: view.sortOrder,
          isDefault: view.isDefault ?? false,
        },
      });
      setRenamingViewId(null);
    } catch (error) {
      console.error('Failed to rename view:', error);
    }
  };

  const handleRenameCancel = () => {
    setRenamingViewId(null);
  };

  const handleRenameMenuItem = () => {
    if (selectedView) {
      setRenamingViewId(selectedView.filterViewId);
    }
    handleCloseMenu();
  };

  const handleDoubleClick = (view: FilterView) => {
    setRenamingViewId(view.filterViewId);
  };

  const handleMenuClick = (
    event: MouseEvent<HTMLButtonElement>,
    view: FilterView,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedView(view);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedView(null);
  };

  const handleDeleteView = async (
    viewId?: string,
    event?: MouseEvent | KeyboardEvent<HTMLElement>,
  ) => {
    event?.stopPropagation();

    // Guard: never call mutation with empty or invalid ID
    if (!viewId) {
      console.error('[Views] Cannot delete view: invalid or missing viewId');
      handleCloseMenu();
      return;
    }

    const { confirmed } = await showConfirm({
      variant: 'delete',
      hideWarningIcon: true,
      hideDescription: true,
      title: t('DeleteViewConfirmation', { viewName: selectedView?.name }),
    });

    if (confirmed) {
      try {
        await deleteViewMutation.mutateAsync(viewId);
        handleCloseMenu();
      } catch (error) {
        console.error('[Views] Error deleting view:', error);
      }
    }
  };

  const handleCopyUrl = async () => {
    if (!selectedView) return;
    try {
      // Build the full URL for the selected view
      const params = new URLSearchParams(window.location.search);

      // Use URLSearchParams.set() to properly encode the filter string
      if (selectedView.filterParameters) {
        params.set('filters', selectedView.filterParameters);
      } else {
        params.delete('filters');
      }

      const fullUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;

      await navigator.clipboard.writeText(fullUrl);
      showSuccess({ message: 'Link copied' });
    } catch (err) {
      console.error('Failed to copy URL:', err);
      showError({ message: 'Failed to copy link. Please try again.' });
    }
    handleCloseMenu();
  };

  const handleDuplicateMenuItem = () => {
    if (selectedView) {
      setViewToDuplicateLocal(selectedView);
    }
    handleCloseMenu();
  };

  const handleSetAsDefault = async () => {
    if (!selectedView) return;

    try {
      // Set the selected view as default
      // Backend will handle clearing other default views and ordering
      await updateView.mutateAsync({
        id: selectedView.filterViewId,
        data: {
          name: selectedView.name,
          filterParameters: selectedView.filterParameters,
          sortOrder: selectedView.sortOrder,
          isDefault: true,
        },
      });

      handleCloseMenu();
    } catch (error) {
      console.error('Failed to set default view:', error);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={1}
      className={styles.Wrapper}
    >
      <ViewsContainer
        currentViewId={currentViewId}
        actions={
          onCreateStart && (
            <IconButton
              variant="contained"
              tooltip={t('NewView')}
              size="tiny"
              color="secondary"
              iconName={uiIcons.actions.add}
              onClick={() => {
                if (onToggleFilterPanel && !filterPanelOpen) {
                  onToggleFilterPanel();
                }
                onCreateStart();
              }}
              disabled={isCreatingView || createView.isPending}
              data-automation-id="create-new-view-button"
              className={styles.CreateButton}
            />
          )
        }
      >
        <ViewItem>
          <ViewLink
            to=""
            isCurrentActiveView={!currentViewId}
            onSelect={() => {
              onViewSelect?.(null);
            }}
            data-automation-id="views-link-all"
          >
            {t('All')}
          </ViewLink>
        </ViewItem>

        {filterViews.length > 0 &&
          filterViews.map((view) => (
            <ViewItem
              key={view.filterViewId}
              data-automation-id={`views-item-${view.filterViewId}`}
              actions={
                renamingViewId !== view.filterViewId ? (
                  <ViewActionsButton
                    id={`views-button-${view.filterViewId}`}
                    aria-controls={
                      anchorEl &&
                      selectedView?.filterViewId === view.filterViewId
                        ? `views-menu-${view.filterViewId}`
                        : undefined
                    }
                    aria-expanded={
                      anchorEl &&
                      selectedView?.filterViewId === view.filterViewId
                        ? 'true'
                        : undefined
                    }
                    onClick={(e) => handleMenuClick(e, view)}
                  />
                ) : undefined
              }
            >
              {renamingViewId === view.filterViewId ? (
                <NameInput
                  value={view.name}
                  onConfirm={(newName) =>
                    handleRenameConfirm(view.filterViewId, newName)
                  }
                  onCancel={handleRenameCancel}
                  defaultName={view.name}
                  autoFocus
                  isLoading={updateView.isPending}
                />
              ) : (
                <ViewLink
                  to={
                    view.filterParameters
                      ? `?filters=${encodeURIComponent(view.filterParameters)}`
                      : '?'
                  }
                  isCurrentActiveView={view.filterViewId === currentViewId}
                  hasUnsavedChanges={hasUnsavedChanges}
                  onSelect={() => onViewSelect?.(view)}
                  onDoubleClick={() => handleDoubleClick(view)}
                  onKeyboardDelete={(e) =>
                    handleDeleteView(view.filterViewId, e)
                  }
                  data-automation-id={`views-link-${view.filterViewId}`}
                >
                  {view.name}
                </ViewLink>
              )}
            </ViewItem>
          ))}

        {/* Dropdown Menu */}
        <Menu
          id={
            selectedView ? `views-menu-${selectedView.filterViewId}` : undefined
          }
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          slotProps={{
            list: {
              'aria-labelledby': selectedView
                ? `views-button-${selectedView.filterViewId}`
                : undefined,
            },
          }}
        >
          <MenuItem
            onClick={handleCopyUrl}
            data-automation-id="views-menu-copy-url"
          >
            <ListItemIcon>
              <Icon name={uiIcons.actions.share} size="small" />
            </ListItemIcon>
            <ListItemText>{t('CopyUrl')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleDuplicateMenuItem}
            data-automation-id="views-menu-duplicate"
          >
            <ListItemIcon>
              <Icon name={uiIcons.actions.duplicate} size="small" />
            </ListItemIcon>
            <ListItemText>{t('Duplicate')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleRenameMenuItem}
            data-automation-id="views-menu-rename"
          >
            <ListItemIcon>
              <Icon name={uiIcons.actions.edit} size="small" />
            </ListItemIcon>
            <ListItemText>{t('Rename')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleSetAsDefault}
            disabled={selectedView?.isDefault}
            data-automation-id="views-menu-set-default"
          >
            <ListItemIcon>
              <Icon name={uiIcons.controls.setDefault} size="small" />
            </ListItemIcon>
            <ListItemText>{t('SetAsDefault')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleDeleteView(selectedView?.filterViewId)}
            disabled={!selectedView}
            data-automation-id="views-menu-delete"
          >
            <ListItemIcon>
              <Icon name={uiIcons.actions.delete} size="small" />
            </ListItemIcon>
            <ListItemText>{t('Delete')}</ListItemText>
          </MenuItem>
        </Menu>
      </ViewsContainer>

      {/* Filter toggle button */}
      {onToggleFilterPanel && (
        <Badge variant="dot" color="secondary" invisible={!hasUnsavedChanges}>
          <IconButton
            tooltip={t('Filters')}
            variant="outlined"
            size="small"
            color="secondary"
            iconName={uiIcons.controls.filter}
            aria-expanded={filterPanelOpen}
            aria-controls="filter-panel"
            onClick={onToggleFilterPanel}
            data-automation-id="filter-toggle-button"
          />
        </Badge>
      )}
    </Stack>
  );
};
